import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from 'react-simple-maps'
import type { ReserveSummary } from '@/types'

const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json'

interface USMapProps {
  reserves: ReserveSummary[]
}

export default function USMap({ reserves }: USMapProps) {
  const navigate = useNavigate()
  const [zoom, setZoom] = useState(1)
  const [tooltip, setTooltip] = useState<{
    reserve: ReserveSummary
    x: number
    y: number
  } | null>(null)

  return (
    <div className="relative w-full overflow-hidden">
      <ComposableMap
        projection="geoAlbersUsa"
        projectionConfig={{ scale: 1100 }}
        width={800}
        height={500}
        style={{ width: '100%', height: 'auto' }}
      >
        <ZoomableGroup onMove={({ zoom: z }) => setZoom(z)}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#1f2937"
                  stroke="#374151"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: 'none' },
                    hover: { fill: '#374151', outline: 'none' },
                    pressed: { outline: 'none' },
                  }}
                />
              ))
            }
          </Geographies>

          {reserves.map((reserve) => {
            const s = 0.7 / zoom
            return (
              <Marker
                key={reserve.id}
                coordinates={[reserve.longitude, reserve.latitude]}
                onClick={() => navigate(`/reserves/${reserve.slug}`)}
                onMouseEnter={(e) => {
                  const event = e as unknown as React.MouseEvent
                  setTooltip({
                    reserve,
                    x: event.clientX,
                    y: event.clientY,
                  })
                }}
                onMouseLeave={() => setTooltip(null)}
                className="cursor-pointer"
              >
                <g transform={`translate(${-12 * s / 0.7}, ${-34 * s / 0.7}) scale(${s})`}>
                  <path
                    d="M17 0C7.6 0 0 7.6 0 17c0 12.7 17 27 17 27s17-14.3 17-27C34 7.6 26.4 0 17 0z"
                    fill="#f7931a"
                    stroke="#fff"
                    strokeWidth={2.5}
                  />
                  <circle cx={17} cy={15} r={6} fill="#fff" />
                </g>
              </Marker>
            )
          })}
        </ZoomableGroup>
      </ComposableMap>

      {tooltip && (
        <div
          className="fixed z-50 bg-gray-800 text-white px-4 py-3 rounded-lg shadow-xl pointer-events-none text-sm"
          style={{
            left: tooltip.x + 12,
            top: tooltip.y - 12,
          }}
        >
          <div className="font-bold text-bitcoin">{tooltip.reserve.name}</div>
          <div className="text-gray-300">
            {tooltip.reserve.city}, {tooltip.reserve.state}
          </div>
          {tooltip.reserve.vault_goal_btc && (
            <div className="text-gray-400 mt-1">
              Goal: {tooltip.reserve.vault_goal_btc} BTC
            </div>
          )}
        </div>
      )}
    </div>
  )
}
